import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Slider, Switch, Select, Tooltip, theme, ConfigProvider } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { IconCircleDot, IconSteeringWheel, IconUserCog, IconTruckDelivery } from '@tabler/icons-react';
import {
  ACCESS_TYPE_OPTIONS,
  HeavyDutyMachineryOption,
} from '../constants/heavyDutyMachinerySpaceOptions';
import { HEAVY_DUTY_MACHINERY_OPTION_ICONS } from '../constants/heavyDutyMachineryOptionIcons';
import TaxonomyPicker from './TaxonomyPicker';
import { FacetCategory, toggleValue } from '../helpers/facetHelpers';
import { IHeavyDutyMachineryFacets, IHeavyDutyMachinerySearchFilters } from '../interfaces/heavyDutyMachinery/ISearch';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import PriceHistogramSlider from './PriceHistogramSlider';
import { filterSliderTheme } from '../constants/filterSliderTheme';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

const { Text } = Typography;

const PRICE_RANGE: [number, number] = [0, 300];
// Exported so HomeSearchBar's "near me" geolocation flow shares the exact
// same radius concept/scale instead of a second, independent one.
export const RADIUS_MAX = 100;
// Fallback bounds for the weight/power sliders when facets.weightTonsRange/
// powerHpRange come back empty (nothing in the catalog has a value yet, or
// the current filters exclude every machine that does) -- 0-30t and
// 0-500hp comfortably cover this launch's 4 focus categories (Excavator,
// ScissorLift, SkidSteerLoader, Dumper) without the slider maxing out on a
// realistic listing. Real facet bounds (see facets prop) are always
// preferred over these when present.
const WEIGHT_TONS_DEFAULT_RANGE: [number, number] = [0, 30];
const POWER_HP_DEFAULT_RANGE: [number, number] = [0, 500];
// Categories with this many options or fewer render as a big-icon tile grid;
// longer lists (currently only Cabin amenities) render as scrollable chips
// instead so the modal doesn't turn into a wall of tiles.
const TILE_MAX_OPTIONS = 10;

interface CategoryConfig {
  key: FacetCategory;
  options: HeavyDutyMachineryOption[];
  titleKey: string;
}

interface CategoryGroup {
  titleKey: string;
  categories: CategoryConfig[];
}

// Category itself (now the 83-leaf taxonomy, see TaxonomyPicker) is
// rendered separately in renderCategoryGroups' own special case for the
// "machinery" group -- it needs the grouped Sector/Family drill-down, not
// the plain tile-grid/chip-row renderCategory() below handles for every
// other (small, flat) facet like accessType.
const CATEGORY_GROUPS: CategoryGroup[] = [
  {
    titleKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SECTION_MACHINERY,
    categories: [],
  },
  {
    titleKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SECTION_ACCESS_RULES,
    categories: [
      { key: 'accessType', options: ACCESS_TYPE_OPTIONS, titleKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_ACCESS_TYPE_LABEL },
    ],
  },
];

interface FiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: IHeavyDutyMachinerySearchFilters;
  facets?: IHeavyDutyMachineryFacets;
  totalCount: number;
  onChange: (patch: Partial<IHeavyDutyMachinerySearchFilters>) => void;
  onClear: () => void;
}

/**
 * /explore's filters overlay -- everything except the top bar's Localidad/
 * Fechas/Modalidad. Every category renders as either a big-icon tile grid
 * (few options) or a horizontally scrollable chip row (many options), same
 * pattern applied consistently across all of them. Every toggle calls
 * onChange immediately (same as the old sidebar checkboxes) so the live
 * facet counts and the "Mostrar N espacios" button stay accurate while the
 * modal is still open -- closing it isn't what applies the filters, it's
 * already been applied.
 */
const FiltersModal: React.FC<FiltersModalProps> = ({ open, onClose, filters, facets, totalCount, onChange, onClear }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();

  // Radius/temperature sliders: `filters` (and the search it feeds) should
  // only update once the handle is released, not on every degree/km the
  // user drags through -- these two mirror the committed filter value but
  // update live on every drag step, purely for the handle position and the
  // number labels. See each Slider's onChange (visual only) vs
  // onChangeComplete (commits via the real onChange prop) below.
  const [radiusDisplay, setRadiusDisplay] = useState<number>(filters.radiusKm ?? RADIUS_MAX);

  useEffect(() => {
    setRadiusDisplay(filters.radiusKm ?? RADIUS_MAX);
  }, [filters.radiusKm]);

  // Real catalog bounds when facets have them, the documented default
  // range otherwise -- see WEIGHT_TONS_DEFAULT_RANGE/POWER_HP_DEFAULT_RANGE.
  const weightBounds: [number, number] =
    facets?.weightTonsRange?.min != null && facets?.weightTonsRange?.max != null
      ? [Math.floor(facets.weightTonsRange.min), Math.ceil(facets.weightTonsRange.max)]
      : WEIGHT_TONS_DEFAULT_RANGE;
  const powerBounds: [number, number] =
    facets?.powerHpRange?.min != null && facets?.powerHpRange?.max != null
      ? [Math.floor(facets.powerHpRange.min), Math.ceil(facets.powerHpRange.max)]
      : POWER_HP_DEFAULT_RANGE;

  const [weightDisplay, setWeightDisplay] = useState<[number, number]>([
    filters.weightTonsMin ?? weightBounds[0],
    filters.weightTonsMax ?? weightBounds[1],
  ]);
  const [powerDisplay, setPowerDisplay] = useState<[number, number]>([
    filters.powerHpMin ?? powerBounds[0],
    filters.powerHpMax ?? powerBounds[1],
  ]);

  useEffect(() => {
    setWeightDisplay([filters.weightTonsMin ?? weightBounds[0], filters.weightTonsMax ?? weightBounds[1]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.weightTonsMin, filters.weightTonsMax, weightBounds[0], weightBounds[1]]);

  useEffect(() => {
    setPowerDisplay([filters.powerHpMin ?? powerBounds[0], filters.powerHpMax ?? powerBounds[1]]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.powerHpMin, filters.powerHpMax, powerBounds[0], powerBounds[1]]);

  const toggleFacet = (category: FacetCategory, value: string) => {
    onChange({ [category]: toggleValue(filters[category], value) } as Partial<IHeavyDutyMachinerySearchFilters>);
  };

  const renderOptionButton = (config: CategoryConfig, option: HeavyDutyMachineryOption, mode: 'tile' | 'chip') => {
    const Icon = HEAVY_DUTY_MACHINERY_OPTION_ICONS[option.value] ?? IconCircleDot;
    const selected = (filters[config.key] ?? []).includes(option.value);
    const countByValue = new Map((facets?.[config.key] ?? []).map((c) => [c.value, c.count]));
    const count = countByValue.get(option.value) ?? 0;
    const borderColor = selected ? token.colorPrimary : token.colorBorderSecondary;
    // token.colorPrimaryBg is antd's own tint of the accent, which is what
    // this wants. It used to be a flat grey instead, because a tint of the
    // old black primary came out nearly black and swallowed the label --
    // with a coral accent the derived tint is the soft fill it was always
    // meant to be.
    const background = selected ? token.colorPrimaryBg : token.colorBgContainer;

    if (mode === 'tile') {
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => toggleFacet(config.key, option.value)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 8,
            padding: '14px 12px',
            borderRadius: 16,
            border: `1.5px solid ${borderColor}`,
            background,
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: selected ? token.colorPrimary : token.colorFillTertiary,
            }}
          >
            <Icon size={19} color={selected ? token.colorWhite ?? '#fff' : token.colorTextSecondary} />
          </div>
          <span style={{ fontSize: 12, lineHeight: 1.3 }}>{translate(t, option.labelKey)}</span>
          {count > 0 && <span style={{ fontSize: 10, color: token.colorTextTertiary }}>{count}</span>}
        </button>
      );
    }

    return (
      <button
        key={option.value}
        type="button"
        onClick={() => toggleFacet(config.key, option.value)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          flexShrink: 0,
          padding: '6px 12px',
          borderRadius: 20,
          border: `1.5px solid ${borderColor}`,
          background,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        <Icon size={16} color={selected ? token.colorPrimary : token.colorTextSecondary} />
        <span style={{ fontSize: 13 }}>
          {translate(t, option.labelKey)} ({count})
        </span>
      </button>
    );
  };

  const renderCategory = (config: CategoryConfig) => {
    const isTileMode = config.options.length <= TILE_MAX_OPTIONS;
    return (
      <div key={config.key} style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, config.titleKey)}
        </Text>
        {isTileMode ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 8 }}>
            {config.options.map((option) => renderOptionButton(config, option, 'tile'))}
          </div>
        ) : (
          <div
            className="no-scrollbar"
            style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {config.options.map((option) => renderOptionButton(config, option, 'chip'))}
          </div>
        )}
      </div>
    );
  };

  // Weight/power: a plain double-handle range Slider (no histogram bars --
  // unlike price, the facet only carries a min/max, not a bucketed
  // distribution), same committed-vs-live-display split as every other
  // slider here (see radiusDisplay above) and the same teal accent (applied
  // by the caller's ConfigProvider, see renderMachinerySpecs).
  const renderRangeSlider = (
    bounds: [number, number],
    display: [number, number],
    setDisplay: (value: [number, number]) => void,
    onCommit: (range: [number, number]) => void,
    formatValue: (value: number) => string
  ) => (
    <div>
      <Slider
        range
        className="heavyDutyMachinery-filter-slider"
        min={bounds[0]}
        max={bounds[1]}
        value={display}
        tooltip={{ formatter: (value) => (value != null ? formatValue(value) : '') }}
        onChange={(value) => setDisplay(value as [number, number])}
        onChangeComplete={(value) => onCommit(value as [number, number])}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Text type="secondary">{formatValue(display[0])}</Text>
        <Text type="secondary">{formatValue(display[1])}</Text>
      </div>
    </div>
  );

  // Weight/power range sliders + the 3 rental-modality toggles -- rendered
  // right under the Category tile grid (see renderCategoryGroups' special
  // case for the "machinery" group) so specs and rental modality read as
  // part of the same "about the machine" section, not a bolted-on extra.
  const renderMachinerySpecs = () => (
    <ConfigProvider theme={filterSliderTheme}>
      <div style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_WEIGHT_TONS_LABEL)}
        </Text>
        {renderRangeSlider(
          weightBounds,
          weightDisplay,
          setWeightDisplay,
          ([min, max]) =>
            onChange({
              weightTonsMin: min === weightBounds[0] ? undefined : min,
              weightTonsMax: max === weightBounds[1] ? undefined : max,
            }),
          (value) => `${value}t`
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_POWER_HP_LABEL)}
        </Text>
        {renderRangeSlider(
          powerBounds,
          powerDisplay,
          setPowerDisplay,
          ([min, max]) =>
            onChange({
              powerHpMin: min === powerBounds[0] ? undefined : min,
              powerHpMax: max === powerBounds[1] ? undefined : max,
            }),
          (value) => `${value}hp`
        )}
      </div>
    </ConfigProvider>
  );

  const renderHireToggleButton = (
    icon: React.ReactNode,
    label: string,
    checked: boolean,
    count: number,
    onClick: () => void
  ) => {
    const borderColor = checked ? token.colorPrimary : token.colorBorderSecondary;
    const background = checked ? 'rgba(0, 0, 0, 0.05)' : token.colorBgContainer;
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={checked}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          flex: '1 1 140px',
          padding: '10px 14px',
          borderRadius: 999,
          border: `1.5px solid ${borderColor}`,
          background,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: checked ? token.colorPrimary : token.colorFillTertiary,
          }}
        >
          {icon}
        </div>
        <span style={{ fontSize: 13 }}>
          {label}
          {count > 0 && <span style={{ color: token.colorTextTertiary }}> ({count})</span>}
        </span>
      </button>
    );
  };

  const renderHireModalityToggles = () => (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_RENTAL_MODALITIES_LABEL)}
      </Text>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {renderHireToggleButton(
          <IconSteeringWheel size={15} color={filters.dryHireOnly ? token.colorWhite ?? '#fff' : token.colorTextSecondary} />,
          translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_DRY),
          !!filters.dryHireOnly,
          facets?.dryHireCount ?? 0,
          () => onChange({ dryHireOnly: !filters.dryHireOnly || undefined })
        )}
        {renderHireToggleButton(
          <IconUserCog size={15} color={filters.wetHireOnly ? token.colorWhite ?? '#fff' : token.colorTextSecondary} />,
          translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_WET_HIRE_ONLY_LABEL),
          !!filters.wetHireOnly,
          facets?.wetHireCount ?? 0,
          () => onChange({ wetHireOnly: !filters.wetHireOnly || undefined })
        )}
        {renderHireToggleButton(
          <IconTruckDelivery size={15} color={filters.deliveryOnly ? token.colorWhite ?? '#fff' : token.colorTextSecondary} />,
          translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_OPT_HIRE_DELIVERY),
          !!filters.deliveryOnly,
          facets?.deliveryCount ?? 0,
          () => onChange({ deliveryOnly: !filters.deliveryOnly || undefined })
        )}
      </div>
    </div>
  );

  // Shared between the mobile full-screen overlay and the desktop popup --
  // only the surrounding chrome (Modal vs. fixed-position screen) differs.
  const renderSliders = () => (
    // Teal accent (see filterSliderTheme, which is the shared tealAccentTheme)
    // scoped to just these three range sliders -- the CTA button below stays
    // on the app's own brand coral.
    <ConfigProvider theme={filterSliderTheme}>
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_PRICE_LABEL)}
        </Text>
        <PriceHistogramSlider
          buckets={facets?.priceHistogram ?? []}
          range={[filters.minPrice ?? PRICE_RANGE[0], filters.maxPrice ?? PRICE_RANGE[1]]}
          max={PRICE_RANGE[1]}
          formatValue={(value) => `${value}€`}
          onChange={([minPrice, maxPrice]) =>
            onChange({
              minPrice: minPrice === PRICE_RANGE[0] ? undefined : minPrice,
              maxPrice: maxPrice === PRICE_RANGE[1] ? undefined : maxPrice,
            })
          }
        />
      </div>

      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_RADIUS_LABEL)}
        </Text>
        <Tooltip title={filters.lat == null ? translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_RADIUS_HINT) : undefined}>
          <div>
            <Slider
              className="heavyDutyMachinery-filter-slider"
              min={1}
              max={RADIUS_MAX}
              disabled={filters.lat == null || filters.lng == null}
              value={radiusDisplay}
              onChange={(value) => setRadiusDisplay(value as number)}
              onChangeComplete={(value) => onChange({ radiusKm: (value as number) === RADIUS_MAX ? undefined : (value as number) })}
            />
            <Text type="secondary">{t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_RADIUS_VALUE, { km: radiusDisplay })}</Text>
          </div>
        </Tooltip>
      </div>

    </ConfigProvider>
  );

  // MachineryType.Slug -> its live facet count (see IHeavyDutyMachineryFacets.category), for TaxonomyPicker's own count badges.
  const categoryFacetCounts: Record<string, number> = Object.fromEntries((facets?.category ?? []).map((c) => [c.value, c.count]));

  const renderCategoryPicker = () => (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CATEGORY_LABEL)}
      </Text>
      <TaxonomyPicker
        multiple
        value={filters.category ?? []}
        onChange={(next) => onChange({ category: next as string[] })}
        facetCounts={categoryFacetCounts}
      />
    </div>
  );

  // Brand is free text on Machinery (see machineryManufacturerBrands.ts's
  // own doc comment), so unlike Category there's no fixed list to enumerate
  // against -- facets.brand already only carries brands actually present in
  // the currently matching catalog (see the backend's ValueFacet), so this
  // Select's own option list *is* "what's on the map right now", with a
  // live count next to each -- exactly what a typed search should offer.
  const renderBrandFilter = () => {
    const brandOptions = (facets?.brand ?? []).map((c) => ({ value: c.value, label: `${c.value} (${c.count})` }));
    return (
      <div style={{ marginBottom: 20 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_MANUFACTURER_BRAND_LABEL)}
        </Text>
        <Select
          mode="multiple"
          allowClear
          showSearch
          style={{ width: '100%' }}
          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_BRAND_PLACEHOLDER)}
          value={filters.brand ?? []}
          onChange={(next) => onChange({ brand: next.length > 0 ? next : undefined })}
          options={brandOptions}
          filterOption={(input, option) => !!option?.value && String(option.value).toLowerCase().includes(input.toLowerCase())}
        />
      </div>
    );
  };

  const renderCategoryGroups = () => (
    <>
      {CATEGORY_GROUPS.map((group) => (
        <div key={group.titleKey} style={{ marginBottom: 8 }}>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
            {translate(t, group.titleKey)}
          </Text>
          {/* Weight/power range + rental modality live under Category, not
              as their own top-level group -- they're all "about the
              machine" the same way Category is, see renderMachinerySpecs. */}
          {group.titleKey === TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SECTION_MACHINERY ? (
            <>
              {renderCategoryPicker()}
              {renderBrandFilter()}
              {renderMachinerySpecs()}
              {renderHireModalityToggles()}
            </>
          ) : (
            group.categories.map(renderCategory)
          )}
        </div>
      ))}
    </>
  );

  const renderVerifiedToggle = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
      <Text>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_VERIFIED_ONLY)} ({facets?.verifiedBadgeCount ?? 0})
      </Text>
      <Switch checked={!!filters.verifiedBadge} onChange={(checked) => onChange({ verifiedBadge: checked || undefined })} />
    </div>
  );

  // Solid coral CTA (this app's accent for primary actions, see CORAL in
  // creamyTokens) instead of antd's default colorPrimary -- same scoped
  // ConfigProvider-override pattern as the sliders' teal accent above,
  // rather than touching the app's own global colorPrimary.
  const renderShowResultsButton = (style?: React.CSSProperties) => (
    <Button type="primary" onClick={onClose} style={{ borderRadius: 999, ...style }}>
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SHOW_RESULTS_BUTTON)} ({totalCount})
    </Button>
  );

  const renderClearFiltersButton = () => (
    <button
      type="button"
      onClick={onClear}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        // 44px touch target via padding, not a bigger/bolder link --
        // the text itself stays exactly the same size and look.
        minHeight: 44,
        padding: '0 4px',
        marginLeft: -4,
        textDecoration: 'underline',
        // Secondary gray, not the app's near-black colorText -- "Limpiar
        // filtros" is a secondary action, same tone as other secondary
        // actions elsewhere (e.g. HomeSearchBar's own "Clear filters").
        color: token.colorTextSecondary,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        font: 'inherit',
      }}
    >
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CLEAR_FILTERS)}
    </button>
  );

  if (isMobile) {
    return (
      <AnimatePresence>
        {open && (
          <motion.div
            key="filters-overlay"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 2000,
              background: token.colorBgContainer,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', flexShrink: 0 }}>
              <Text strong style={{ fontSize: 16 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
              </Text>
              <button
                type="button"
                aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_OVERLAY_CLOSE)}
                onClick={onClose}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: 'none',
                  background: token.colorFillTertiary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <CloseOutlined style={{ fontSize: 16 }} />
              </button>
            </div>

            <div
              className="no-scrollbar"
              style={{ flex: 1, overflowY: 'auto', padding: '0 20px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {renderSliders()}
              {renderCategoryGroups()}
              {renderVerifiedToggle()}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                flexShrink: 0,
              }}
            >
              {renderClearFiltersButton()}
              {renderShowResultsButton()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      // antd's own Modal already caps width to `calc(100vw - 16px)` below
      // ~575px, but has no such safety net above that -- at 768px this
      // fixed 760px left almost no breathing room from the viewport edges.
      // min() keeps the same 760px on desktop (where 92vw is always wider)
      // while giving tablet/narrow widths real margin instead of just
      // barely fitting.
      width="min(760px, 92vw)"
      transitionName="filters-slide"
      maskTransitionName="filters-fade"
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
      // Hides the body's native scrollbar track (see .no-scrollbar) --
      // purely cosmetic, scroll still works the same via wheel/touch/keys.
      classNames={{ body: 'no-scrollbar' }}
      styles={{
        content: {
          borderRadius: 20,
          border: '0.5px solid rgba(0,0,0,0.08)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        },
        body: { maxHeight: '65vh', overflowY: 'auto', padding: '0 28px', scrollbarWidth: 'none', msOverflowStyle: 'none' },
      }}
    >
      {renderSliders()}
      {renderCategoryGroups()}
      {renderVerifiedToggle()}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: token.colorBgContainer,
          margin: '16px -28px 0',
          padding: '16px 28px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {renderClearFiltersButton()}
        {renderShowResultsButton()}
      </div>
    </Modal>
  );
};

export default FiltersModal;
